import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APIProvider, Map, MapCameraChangedEvent, useMap } from '@vis.gl/react-google-maps';
import type { LayersList } from '@deck.gl/core';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { Search } from '@mui/icons-material';
import { Box, Button, InputAdornment, Stack, TextField } from '@mui/material';

import { GOOGLE_API_KEY, GOOGLE_MAPS_MAP_ID_ADMI_MAP } from 'src/appConfig';
import useDebouncer from 'src/common/useDebouncer';
import useGoogleMapsApiLoader from 'src/infrastructure/google/useGoogleMapsApiLoader';

type DeckglOverlayProps = { layers?: LayersList };

export function DeckGlOverlay({ layers }: DeckglOverlayProps) {
    const deck = useMemo(
        () =>
            new GoogleMapsOverlay({
                interleaved: false,
                getTooltip: (info) => {
                    const text = info.object?.properties?.tooltip;
                    if (!text) return null;
                    // Offset via margin (relative to the mouse position that DeckGL sets
                    // as `left`/`top` on each hover) so the tooltip sits just to the
                    // bottom-right of the cursor. `transform` was stripping DeckGL's
                    // positioning logic on some browsers.
                    return {
                        text,
                        style: {
                            marginLeft: '16px',
                            marginTop: '12px',
                            whiteSpace: 'pre',
                        },
                    };
                },
            }),
        [],
    );

    const map = useMap();
    useEffect(() => {
        deck.setMap(map);
        return () => deck.setMap(null);
    }, [map, deck]);
    useEffect(() => deck.setProps({ layers }), [layers, deck]);

    // no dom rendered by this component
    return null;
}

// Child component that uses useMap() to pan the map programmatically for address search.
// This avoids controlling the map via a `center` prop, which would interrupt user drag gestures.
function MapPanController({ target }: { target: google.maps.LatLngLiteral | null }) {
    const map = useMap();
    const [mapReady, setMapReady] = useState(false);

    // `useMap()` hands back the map instance before it has settled its initial
    // camera. A `panTo` issued in that window is overridden when the map applies
    // its uncontrolled `defaultCenter`, so an early target is silently lost.
    // This is the deep-link bug: when the geocode resolves faster than the map
    // initialises (warm cache), the map stays at `defaultCenter` instead of the
    // REA address — intermittently, depending on which finishes first. Gate the
    // pan on the map's first `idle` so a target set early still lands once ready.
    //
    // Reset `mapReady` in the cleanup so that if the underlying map instance is
    // ever swapped, the gate re-arms against the *new* map's first idle instead
    // of panning a still-initialising map with a stale `true`. The listener is
    // removed only in cleanup (not from inside its own callback) to avoid a
    // temporal-dead-zone reference if `idle` were to fire synchronously;
    // `setMapReady(true)` is idempotent, so re-firing on later idles is a no-op.
    useEffect(() => {
        if (!map) return;
        const listener = map.addListener('idle', () => setMapReady(true));
        return () => {
            listener.remove();
            setMapReady(false);
        };
    }, [map]);

    useEffect(() => {
        if (map && mapReady && target) {
            map.panTo(target);
        }
    }, [map, mapReady, target]);
    return null;
}

type AdmiMapProps = {
    layers: LayersList;
    defaultCenter?: google.maps.LatLngLiteral;
    onCenterChanged?: (center: google.maps.LatLngLiteral) => void;
    /** Fired when the user submits an address search (not on pan/zoom). */
    onAddressSearch?: (center: google.maps.LatLngLiteral) => void;
    /**
     * Programmatic pan target. When this changes to a non-null value the map
     * smoothly pans to it (same path used by the embedded address-search bar
     * — see `MapPanController`). Setting back to null is a no-op. Use this
     * for deep-link landings, "fit to selection" buttons, etc. — anything
     * that should move the map without going through the search bar.
     */
    panTo?: google.maps.LatLngLiteral | null;
    mapHeight?: string;
    /** CSS cursor applied to the map container. Useful for showing a `pointer` when hovering an interactive layer. */
    cursor?: string;
    /** Optional content rendered directly below the search bar and above the map (e.g. action buttons). */
    headerActions?: React.ReactNode;
};

export default function AdmiMap({
    layers,
    defaultCenter,
    onCenterChanged,
    onAddressSearch,
    panTo,
    mapHeight = '40rem',
    cursor,
    headerActions,
}: AdmiMapProps) {
    const { t } = useTranslation();
    const [address, setAddress] = useState('');
    const [panTarget, setPanTarget] = useState<google.maps.LatLngLiteral | null>(null);
    const loader = useGoogleMapsApiLoader();

    const debounce = useDebouncer();

    // Forward the external `panTo` prop into the internal panTarget. Both
    // sources (address bar + external) flow through the same MapPanController
    // below, so the consumer's panTo and a user address search compose
    // naturally — the last one wins. The internal address-bar handler
    // (`handleChangeAddress`) writes `panTarget` synchronously after async
    // geocoding, so we can't derive `panTarget` purely from `panTo` — both
    // writers have to share the cell.
    useEffect(() => {
        if (panTo) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setPanTarget(panTo);
        }
    }, [panTo]);

    function handleCenterChanged(event: MapCameraChangedEvent) {
        if (onCenterChanged) {
            debounce(() => onCenterChanged(event.detail.center), 1000);
        }
    }

    async function handleChangeAddress(e: React.KeyboardEvent | React.MouseEvent) {
        if (e.type === 'click' || (e as React.KeyboardEvent).key === 'Enter') {
            const { Geocoder } = (await loader.importLibrary('geocoding')) as google.maps.GeocodingLibrary;
            const geocoder = new Geocoder();

            // `geocoder.geocode` can resolve to undefined when the Google Maps API
            // failed to bootstrap cleanly (e.g. NotLoadingAPIFromGoogleMapsError),
            // so guard the response before dereferencing `results`.
            const geocoderResponse = await geocoder.geocode({ address, language: 'de' }).catch(() => undefined);
            if (geocoderResponse && geocoderResponse.results.length) {
                const r = geocoderResponse.results[0];
                const newCenter = { lat: r.geometry.location.lat(), lng: r.geometry.location.lng() };
                setPanTarget(newCenter);
                (onAddressSearch ?? onCenterChanged)?.(newCenter);
            }
        }
    }

    return (
        <APIProvider apiKey={GOOGLE_API_KEY}>
            <Stack direction="row" spacing={1}>
                <TextField
                    placeholder={t('Search')}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onKeyDown={handleChangeAddress}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        },
                    }}
                    // Chrome paints autofilled address fields with a pale-blue background
                    // (`:-webkit-autofill`). Override it with a white inset box-shadow so the
                    // search bar stays white after a previously-searched address is filled in.
                    sx={{
                        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
                            {
                                WebkitBoxShadow: '0 0 0 100px #fff inset',
                                WebkitTextFillColor: 'inherit',
                                caretColor: 'inherit',
                            },
                    }}
                    fullWidth
                    size="small"
                />
                <Button variant="contained" color="primary" onClick={handleChangeAddress}>
                    {t('Search')}
                </Button>
            </Stack>

            {headerActions && <Box sx={{ mt: 1 }}>{headerActions}</Box>}

            <Map
                mapId={GOOGLE_MAPS_MAP_ID_ADMI_MAP}
                mapTypeId="satellite"
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={false}
                style={{ width: '100%', height: mapHeight, marginTop: '0.5rem' }}
                defaultCenter={defaultCenter}
                defaultZoom={17}
                renderingType="VECTOR"
                draggableCursor={cursor}
                onCenterChanged={handleCenterChanged}
            >
                <MapPanController target={panTarget} />
                <DeckGlOverlay layers={layers} />
            </Map>
        </APIProvider>
    );
}
